import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { BucketSource, RootStateTypeExtra } from '@store/types'

import {
  FloorPlateGalleryInterface,
  selectFromResult,
  useGetFloorPlateGalleryQuery,
} from '@api/floor-plate-gallery'

import { FileType } from '@adUtilities/constants'
import { ProjectType } from '@adUtilities/constants/common'

const useGetFloorPlateGalleries = () => {
  const {
    projectName,
    activeBlock,
    activeLevel,
    projectType,
    activePrecinctId,
    activeStageId,
  } = useSelector((state: RootStateTypeExtra) => ({
    projectName: state.projectIdentity.projectName,
    projectType: state.projectIdentity.type,
    activeBlock: state.firestore?.session?.building?.activeBlock || '',
    activeLevel: state.firestore?.session?.building?.activeLevel || '',
    activePrecinctId:
      state.firestore?.session?.houseAndLand?.activePrecinctId || '',
    activeStageId: state.firestore?.session?.houseAndLand?.activeStageId || '',
  }))

  const { isLoaded, floorPlateGalleryData } = useGetFloorPlateGalleryQuery(
    { projectName },
    {
      selectFromResult,
    }
  )

  const matchHouseAndLand = (value: FloorPlateGalleryInterface) => {
    if (activePrecinctId) {
      return (
        (value?.precinct || '').toString() === activePrecinctId &&
        (value?.stage || '').toString() === activeStageId
      )
    }

    return (value?.stage || '').toString() === activeStageId
  }

  const matchApartment = (value: FloorPlateGalleryInterface) => {
    if (activeBlock) {
      return (
        (value?.block || '').toString() === activeBlock &&
        (value?.level || '').toString() === activeLevel
      )
    }

    return (value?.level || '').toString() === activeLevel
  }

  const slides = useMemo(() => {
    if (floorPlateGalleryData.length < 1) {
      return []
    }

    const foundData = floorPlateGalleryData.find((value) => {
      if (projectType === ProjectType.HouseAndLand) {
        return matchHouseAndLand(value)
      }

      return matchApartment(value)
    })

    if (foundData === null) {
      return []
    }

    const floorAssets = foundData?.assets || []

    if (floorAssets.length === 0) {
      return []
    }

    return floorAssets.map((item) => ({
      id: item.id,
      src: item.imageSrc || '',
      videoSource: item.videoSrc,
      thumbnail: item.imageSrc,
      fileType: item.type as FileType,
      bucketType: BucketSource.Legacy,
      noSpliceUrl: false,
    }))
  }, [
    activeLevel,
    activeBlock,
    activePrecinctId,
    activeStageId,
    floorPlateGalleryData,
  ])

  return {
    slides,
    isLoaded,
  }
}

export default useGetFloorPlateGalleries
